/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react'

/** Material UI **/
import {
  Box,
  Button,
  IconButton,
  // InputAdornment,
  Link,
  makeStyles,
  Tooltip
  // OutlinedInput
} from '@material-ui/core'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { alpha } from '@mui/material/styles'
import customTheme from '../../styles/mui_theme'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles(theme => ({
  bottomSpacing: {
    marginBottom: 10
  },
  button: {
    backgroundColor: theme.colors.iconBlue,
    color: theme.colors.white,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '14px',
    borderRadius: '100px',
    marginTop: '2em',
    padding: '1em',
    minWidth: '8em',
    [theme.breakpoints.down('xs')]: {
      width: '10em'
    }
  },
  buttonError: {
    backgroundColor: theme.colors.white,
    color: theme.colors.errorColor,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '14px',
    borderRadius: '100px',
    marginTop: '2em',
    padding: '1em',
    minWidth: '8em',
    border: '1px dashed ' + theme.colors.errorColor
  },
  removeButton: {
    fontSize: '12px'
  },
  input: {
    borderRadius: 45,
    padding: '0.6em',
    borderColor: theme.colors.profile.border_input,
    'label + &': {
      marginTop: theme.spacing(3)
    },
    '& .MuiInputBase-input': {
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      fontSize: 12
    },
    '& .MuiOutlinedInput-input': {
      padding: '0px'
    }
  },
  error: {
    marginLeft: '5px',
    marginTop: '0.5em',
    fontSize: '10px',
    color: theme.colors.errorText
  },
  label: {
    marginTop: '0.1em',
    fontSize: '12px',
    color: theme.colors.grey,
    marginLeft: '5px',
    transform: 'scale(1)'
  },
  labelWithTooltip: {
    marginTop: '0.1em',
    fontSize: '12px',
    color: theme.colors.grey,
    marginLeft: '5px',
    transform: 'scale(1)'
  },
  infoIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '10px',
    cursor: 'pointer',
    transform: 'translateY(5px)'
  },
  infoIcon: {
    fontSize: '21px',
    pointerEvents: 'none'
  },
  orangeIcon: {
    color: theme.colors.icon_orange
  },
  greyIcon: {
    color: theme.colors.grey
  },
  tooltip: {
    backgroundColor: theme.colors.dark_blue,
    color: theme.colors.white,
    fontSize: '12px',
    lineHeight: '19px',
    padding: '8px',
    maxWidth: '400px',
    '& .MuiTooltip-arrow': {
      color: theme.colors.dark_blue
    }
  },
  linkContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    marginTop: '2.05em',
    border: '1px solid ' + theme.colors.profile.border_input,
    padding: '0.8em',
    borderRadius: 45,
    minWidth: '15em',
    alignItems: 'center',
    '&.disabled': {
      backgroundColor: customTheme.colors.disable_background,
      cursor: 'not-allowed'
    }
  },
  fileData: {
    maxWidth: '12em',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    paddingLeft: '0.3em'
  },
  required: {
    color: theme.colors.errorText
  },
  buttonRequired: {
    backgroundColor: theme.colors.iconBlue,
    color: theme.colors.white,
    border: '1px solid ' + theme.colors.errorText,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '14px',
    borderRadius: '100px',
    marginTop: '2em',
    padding: '1em',
    minWidth: '8em'
  },
  requestChangeButton: {
    position: 'absolute',
    top: '25px',
    right: '0',
    left: '230px',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    textTransform: 'none',
    borderRadius: '45px',
    fontSize: '12px',
    width: '140px',
    height: '36px',
    minWidth: 'auto',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.9)
    },
    [theme.breakpoints.up(1920)]: {
      left: '260px'
    }
  },
  helperText: {
    color: theme.colors.errorText,
    filter: 'brightness(90%)',
    fontSize: '10px',
    marginTop: '4px',
    marginLeft: '5px'
  }
}))

export default function GlobalUploadFile(props) {
  const classes = useStyles()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (props.error) setLoading(false)
  }, [props.value, props.error])

  const handleChange = event => {
    setLoading(true)
    props.onChange(event?.target?.files[0], props.field)
  }

  const handleClick = event => {
    props.handleClear(props.field)
    setLoading(false)
  }

  const handleFileDownload = event => {
    props.handleFileDownload(event, props.value, props.fileName)
  }

  const renderInfoIcon = () => {
    if (props.showTooltip && props.tooltipText) {
      return (
        <Tooltip
          title={props.tooltipText}
          placement="right"
          arrow
          PopperProps={{
            modifiers: {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport'
              }
            }
          }}
          classes={{
            tooltip: classes.tooltip
          }}
        >
          <div className={classes.infoIconWrapper}>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className={`${classes.infoIcon} ${props.hasPendingChanges ? classes.orangeIcon : classes.greyIcon}`}
            />
          </div>
        </Tooltip>
      )
    }
    return null
  }

  const showRequestButton = props.disabled

  return (
    <FormControl variant="standard" fullWidth={true}>
      <InputLabel shrink className={props.showTooltip ? classes.labelWithTooltip : classes.label}>
        {props.required && <span className={classes.required}>*</span>}
        {props.label}
        {renderInfoIcon()}
      </InputLabel>
        {props.value && !props.error ? (
          <Box
            className={`${classes.linkContainer} ${props?.customClasses?.uploadBtn} ${props.disabled ? 'disabled' : ''}`}
            style={props.highlightRequired ? { border: '1px solid red' } : {}}
          >
            <Link
              className={classes.fileData}
              onClick={async event => await handleFileDownload(event)}
              underline="hover"
              target="_blank"
              rel="noopener"
            >
              {props.fileName ?? 'file'}
            </Link>
            <IconButton
              size="small"
              className={classes.removeButton}
              onClick={handleClick}
              disabled={props.disabled}
            >
              <FontAwesomeIcon icon="fa-regular fa-circle-xmark" />
            </IconButton>
          </Box>
        ) : (
          <Button
            id="bootstrap-input"
            variant="contained"
            component="label"
            disabled={loading || props.disabled}
            className={`${
              props.error
                ? classes.buttonError
                : props.required
                ? classes.buttonRequired
                : classes.button
            } ${props?.customClasses?.uploadBtn}`}
          >
            {loading
              ? props.loadingLabel
              : props.error
              ? props.helperText
              : props.buttonLabel}
            <input
              hidden
              accept={props.accept}
              multiple
              type="file"
              onChange={handleChange}
            />
          </Button>
        )}
        {props.isW9Field && props.highlightRequired && (
          <div className={classes.helperText}>
            {t('company_profile.requires_approval.changing_ein_w9')}
          </div>
        )}
      {showRequestButton && (
        <Button
          variant="contained"
          className={classes.requestChangeButton}
          disableElevation
          onClick={() => props.onRequestChange?.(props.field)}
        >
          {t('company_profile.requires_approval.request_change')}
        </Button>
      )}
      <div className={classes.bottomSpacing}></div>
    </FormControl>
  )
}
